import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";

// ─────────────────────────────────────────────────────────────────────────────
// Lights Out — junk wax cosmetic effect
//
// Triggered by a `junk_wax_event` WS broadcast (variant = lights_out).
// The full game panel below the scoreboard snaps dark and STAYS dark until the
// inning ends or the user dismisses the card via the banner X button.
//
// Z-index contract (do not change without updating all four):
//   z-[400]  HR ball cinematic
//   z-[450]  THIS overlay  ← covers <main>, sits below scoreboard
//   z-[460]  Elements that punch THROUGH (see below)
//   z-[500]  Scoreboard nav (always above everything)
//
// Elements at z-[460] that stay lit and clickable during the effect:
//   • Weather strip + pitch clock + game stats  (DiamondField.jsx)
//   • Active manager-card banner                (GameBoard.jsx)
//   • Phase controls panel (phase tabs, card hand, phase badge) (GameBoard.jsx)
//   • Left play-by-play drawer wrapper          (GameBoard.jsx)
//   • Right roster-panel wrapper                (GameBoard.jsx)
//
// The overlay is pointer-events-none — nothing is locked out.
// ─────────────────────────────────────────────────────────────────────────────

// ── Tunable constants ─────────────────────────────────────────────────────────

// Peak overlay opacity while the lights are out (0 = invisible, 1 = solid).
// 0.82 = dark enough to feel like stadium lights out, but the field still
// bleeds through faintly. Drop to ~0.65 for a subtler "lights dim" feel.
const DARK_PEAK = 0.82;

// How fast the lights snap OFF (seconds). Keep this very short — stadium
// power cuts are instant. Raise to ~0.5 for a slow dim-down instead.
const SNAP_IN_S = 0.12;

// How long the fade back to normal takes when the card is dismissed (seconds).
// Longer = more dramatic power-restore feel.
const FADE_OUT_S = 1.8;

// ─────────────────────────────────────────────────────────────────────────────

export default function LightsOutEffect({ active }) {
  const [rect, setRect] = useState(null);

  useEffect(() => {
    if (!active) return;
    // Size to <main> so the overlay spans the full game panel below the
    // scoreboard — not just the diamond square. Fallbacks for edge cases.
    const el = document.querySelector("main")
            || document.querySelector('[data-testid="field-area"]')
            || document.querySelector('[data-testid="diamond-field"]');
    if (!el) return;
    const r = el.getBoundingClientRect();
    setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
  }, [active]);

  return (
    <AnimatePresence>
      {active && rect && (
        <motion.div
          // pointer-events-none: the overlay never intercepts clicks — all
          // buttons, cards, and controls remain fully interactive during the effect.
          className="pointer-events-none fixed z-[450] bg-slate-950"
          style={rect}
          initial={{ opacity: 0 }}
          animate={{ opacity: DARK_PEAK }}
          exit={{ opacity: 0, transition: { duration: FADE_OUT_S, ease: "easeIn" } }}
          transition={{ duration: SNAP_IN_S, ease: "linear" }}
        />
      )}
    </AnimatePresence>
  );
}
